/**
 * Which shop's look is showing, driven by a `data-shop-theme` attribute on `<html>` — the same
 * mechanism `themeSlice.applyTheme` uses for light/dark, deliberately. The two attributes sit on
 * the same element and the stylesheet resolves them together, so a shop's colour survives a
 * light/dark switch and a dark page never wears a light theme's fill.
 *
 * Unlike light/dark there is no slice and nothing in `localStorage`: this is not a preference.
 * It is a property of the shop the signed-in user belongs to, it arrives on the session, and it
 * is never meant to outlive the tab.
 *
 * ## The colour scheme, and who wins
 *
 * A look is designed for one scheme — OriTech for light, Diamond for dark. That default is
 * applied here, with one strict precedence:
 *
 *   1. the user's own saved choice (`pos.theme` in localStorage, set by the toggle)
 *   2. the shop's `defaultColorScheme`
 *   3. light
 *
 * The shop default is applied by setting `data-theme` directly and **never by writing
 * `pos.theme`**. Writing it would turn the shop's preference into the user's, and the next time
 * they toggled they would be undoing a choice they never made. `themeSlice` still reports
 * "system" for such a device, which is the truth: they have not chosen.
 *
 * ## Why this replaced a hash
 *
 * The first version of this file derived an accent by hashing the shop's id into one of two
 * colours. That gave every shop *a* colour with no admin screen, which was the right trade while
 * there was nothing to configure — but it could not give a shop *its own* colour, and nobody
 * could change one. A shop's branding is now what its owner chose when the shop was created.
 */

import { DEFAULT_SHOP_THEME, NEUTRAL_THEME_CODE, shopThemeFor } from '@pos/tokens';

/** The key `themeSlice` writes. Read here, never written — see the precedence note above. */
const USER_CHOICE_KEY = 'pos.theme';

function userHasChosenScheme(): boolean {
  try {
    const stored = localStorage.getItem(USER_CHOICE_KEY);
    return stored === 'light' || stored === 'dark';
  } catch {
    // Storage disabled: nothing is saved, so nothing outranks the shop default.
    return false;
  }
}

/**
 * Applied to `<html>`. Callers wire this into an effect keyed on whatever their shop-scoped
 * queries key on, so the colour changes in the same commit as the data rather than a beat later.
 *
 * Always sets the attribute, including for the default look. The default's variables are emitted
 * explicitly for exactly this reason — switching *back* to it has to repaint, and an "absent
 * means default" rule leaves a shop wearing the previous shop's colour the moment one caller
 * forgets to remove the attribute.
 */
export function applyShopTheme(themeCode: string | null | undefined): void {
  const root = document.documentElement;

  // Through the same tolerant lookup the rest of the app uses, so an unknown code paints the
  // default rather than leaving `<html>` claiming a theme no stylesheet defines.
  const theme = shopThemeFor(themeCode ?? DEFAULT_SHOP_THEME);

  root.dataset.shopTheme = theme.code;

  if (!userHasChosenScheme()) {
    root.dataset.theme = theme.defaultColorScheme;
  }
}

/**
 * The neutral state: no shop, no scheme opinion. For a sign-in screen on a device that has never
 * signed in, or one whose remembered branding was just cleared.
 *
 * Paints the neutral look rather than removing the attribute. Removing it was the original
 * behaviour and it was wrong: with no `data-shop-theme` the page falls through to `:root`, and
 * `:root` is OriTech — the base palette is built from the logo's moss and copper. So a signed-out
 * Diamond till showed OriTech's green, and "no shop" was indistinguishable from "the first shop".
 * `neutralTheme` exists to be what is underneath.
 *
 * The scheme is still cleared — only where the user has not chosen — so the neutral page follows
 * the OS rather than the last shop. The neutral look is emitted for both schemes, so it has
 * somewhere to land either way.
 */
export function clearShopTheme(): void {
  const root = document.documentElement;

  root.dataset.shopTheme = NEUTRAL_THEME_CODE;

  if (!userHasChosenScheme()) {
    delete root.dataset.theme;
  }
}
